import { useState, useEffect, useRef } from 'react';
import logo from '@/assets/drahft_logo.webp';

const STUDIO_URL = import.meta.env.DEV ? 'http://localhost:3001' : 'https://studio.drahft.com';

const SOLUTIONS_LINKS = [
  { label: 'Wedding Photographers', href: '/solutions/wedding-photographers' },
  { label: 'Wedding Videographers', href: '/solutions/wedding-videographers' },
  { label: 'Wedding Venues', href: '/solutions/wedding-venues' },
  { label: 'Wedding Planners', href: '/solutions/wedding-coordinators' },
  { label: 'Wedding Florists', href: '/solutions/wedding-florists' },
  { label: 'DJs & Musicians', href: '/solutions/wedding-djs' },
  { label: 'Hair & Makeup Artists', href: '/solutions/hair-makeup-artists' },
  { label: 'Wedding Officiants', href: '/solutions/wedding-officiants' },
  { label: 'Calligraphers', href: '/solutions/wedding-calligraphers' },
  { label: 'Decor & Rentals', href: '/solutions/decor-rentals' },
  { label: 'Photo Booths', href: '/solutions/photo-booth' },
  { label: 'Transportation', href: '/solutions/wedding-transportation' },
  { label: 'Bridal Boutiques', href: '/solutions/bridal-boutiques' },
];

const INTEGRATIONS_LINKS = [
  // Popular platforms
  { label: 'WordPress', href: '#' },
  { label: 'Showit', href: '#' },
  { label: 'Webflow', href: '#' },
  { label: 'Wix', href: '#' },
  { label: 'Squarespace', href: '#' },
  // Photography platforms
  { label: 'Pixieset', href: '#' },
  { label: 'Pic Time', href: '#' },
  { label: 'SmugMug', href: '#' },
  { label: 'Zenfolio', href: '#' },
  { label: 'PhotoShelter', href: '#' },
  { label: 'Format', href: '#' },
  { label: 'Focal', href: '#' },
  { label: 'FreshThemes', href: '#' },
  // Ecommerce platforms
  { label: 'Shopify', href: '#' },
  { label: 'WooCommerce', href: '#' },
  { label: 'BigCommerce', href: '#' },
  { label: 'Magento', href: '#' },
  { label: 'Ecwid', href: '#' },
  { label: 'Tonic Site Shop', href: '#' },
  // CMS & blogging platforms
  { label: 'Joomla', href: '#' },
  { label: 'Drupal', href: '#' },
  { label: 'Ghost', href: '#' },
  { label: 'Blogger', href: '#' },
  { label: 'Tumblr', href: '#' },
  { label: 'Yola', href: '#' },
];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  const solutionsRef = useRef<HTMLLIElement>(null);
  const integrationsRef = useRef<HTMLLIElement>(null);

  // Close menu on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setSolutionsOpen(false);
        setIntegrationsOpen(false);
      }
    };

    if (menuOpen || solutionsOpen || integrationsOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen, solutionsOpen, integrationsOpen]);

  // Close solutions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (solutionsRef.current && !solutionsRef.current.contains(e.target as Node)) {
        setSolutionsOpen(false);
      }
    };
    if (solutionsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [solutionsOpen]);

  // Close integrations dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (integrationsRef.current && !integrationsRef.current.contains(e.target as Node)) {
        setIntegrationsOpen(false);
      }
    };
    if (integrationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [integrationsOpen]);

  return (
    <header
      id="masthead"
      className="fixed top-0 left-0 right-0 z-50 font-sans border-b site-header bg-white/95 backdrop-blur-md border-neutral-200"
    >
      <div className="top-navigation">
        <div className="px-4 py-3 mx-auto max-w-7xl md:py-4">
          <div className="flex items-center justify-between w-full gap-4 site-identity md:gap-8">
            {/* Logo and Site Title/Description - LEFT */}
            <div className="flex items-center gap-4 site-title-wrap">
              <a href="/" className="custom-logo-link" aria-current="page">
                <img
                  width={160}
                  height={60}
                  src={logo}
                  className="custom-logo"
                  alt="drahft"
                  decoding="async"
                  style={{ maxHeight: 40, width: 'auto', height: 'auto' }}
                />
              </a>
            </div>

            {/* Main Navigation - CENTER (hidden on mobile) */}
            <nav className="justify-center flex-1 hidden main-navigation md:flex" aria-label="Main">
              <ul className="flex items-center gap-8 p-0 m-0 list-none">
                <li>
                  <a
                    href="/pricing/"
                    className="transition-colors text-md text-neutral-700 hover:text-black"
                  >
                    Plans & Features
                  </a>
                </li>
                <li ref={solutionsRef} className="relative">
                  <button
                    type="button"
                    className="flex items-center gap-1 transition-colors text-md text-neutral-700 hover:text-black"
                    aria-haspopup="true"
                    aria-expanded={solutionsOpen}
                    onClick={() => setSolutionsOpen(prev => !prev)}
                  >
                    Solutions
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      width={16}
                      height={16}
                      className={`transition-transform duration-200 ${solutionsOpen ? 'rotate-180' : ''}`}
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {solutionsOpen && (
                    <div
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[480px] bg-white rounded-2xl shadow-soft border border-neutral-200 z-50 p-5"
                      role="menu"
                    >
                      <p className="px-1 mb-3 text-xs font-semibold tracking-wider uppercase text-neutral-400">
                        Wedding Vendor Solutions
                      </p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        {SOLUTIONS_LINKS.map(link => (
                          <a
                            key={link.href}
                            href={link.href}
                            role="menuitem"
                            className="flex items-center gap-2 px-3 py-2 text-sm transition-colors rounded-lg text-neutral-800 hover:bg-neutral-100 hover:text-black"
                            onClick={() => setSolutionsOpen(false)}
                          >
                            {link.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </li>
                <li ref={integrationsRef} className="relative">
                  <button
                    type="button"
                    className="flex items-center gap-1 transition-colors text-md text-neutral-700 hover:text-black"
                    aria-haspopup="true"
                    aria-expanded={integrationsOpen}
                    onClick={() => setIntegrationsOpen(prev => !prev)}
                  >
                    Integrations
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      width={16}
                      height={16}
                      className={`transition-transform duration-200 ${integrationsOpen ? 'rotate-180' : ''}`}
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {integrationsOpen && (
                    <div
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[600px] bg-white rounded-2xl shadow-soft border border-neutral-200 z-50 p-5"
                      role="menu"
                    >
                      <p className="px-1 mb-3 text-xs font-semibold tracking-wider uppercase text-neutral-400">
                        Integrations
                      </p>
                      <div className="grid grid-cols-3 gap-x-4 gap-y-1">
                        {INTEGRATIONS_LINKS.map(link => (
                          <a
                            key={link.href}
                            href={link.href}
                            role="menuitem"
                            className="flex items-center gap-2 px-3 py-2 text-sm transition-colors rounded-lg text-neutral-800 hover:bg-neutral-100 hover:text-black"
                            onClick={() => setIntegrationsOpen(false)}
                          >
                            {link.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </li>
              </ul>
            </nav>

            {/* Auth Buttons - RIGHT */}
            <div className="flex items-center gap-4 top-navigation-right">
              <a
                href={`${STUDIO_URL}/login`}
                className="hidden px-3 py-2 transition-colors md:inline text-md text-neutral-700 hover:text-black"
              >
                Sign In
              </a>
              <a
                href={`${STUDIO_URL}/register`}
                className="hidden text-center rounded-lg btn-primary btn-small md:inline"
              >
                Get it free
              </a>
              {/* Mobile menu toggle */}
              <button
                type="button"
                className="ml-2 menu-drawer-toggle md:hidden text-neutral-900"
                aria-label="Toggle menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  width={28}
                  height={28}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation Drawer */}
          {menuOpen && (
            <>
              {/* Backdrop */}
              <div
                role="presentation"
                className="fixed bottom-0 left-0 right-0 z-40 top-16 bg-black/20 md:hidden"
                onClick={() => setMenuOpen(false)}
              />
              <nav
                className="main-navigation is-active flex flex-col md:hidden bg-white border-b border-neutral-200 shadow-lg absolute inset-x-0 top-[100%] z-50 max-h-[calc(100vh-64px)] overflow-y-auto"
                aria-label="Mobile Main"
              >
                <ul className="flex flex-col w-full divide-y divide-neutral-100">
                  <li>
                    <a
                      href="/pricing/"
                      className="block px-6 py-4 text-base font-medium transition-colors text-neutral-900 hover:bg-neutral-50"
                    >
                      Plans & Features
                    </a>
                  </li>
                  <li>
                    <a
                      href="/solutions/"
                      className="block px-6 py-4 text-base font-medium transition-colors text-neutral-900 hover:bg-neutral-50"
                    >
                      Solutions
                    </a>
                    <ul className="divide-y divide-neutral-100 bg-neutral-50">
                      {SOLUTIONS_LINKS.map(link => (
                        <li key={link.href}>
                          <a
                            href={link.href}
                            className="block px-8 py-3 text-sm transition-colors text-neutral-600 hover:bg-neutral-100"
                          >
                            {link.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </li>
                  <li>
                    <a
                      href="/integrations/"
                      className="block px-6 py-4 text-base font-medium transition-colors text-neutral-900 hover:bg-neutral-50"
                    >
                      Integrations
                    </a>
                    <ul className="divide-y divide-neutral-100 bg-neutral-50">
                      {INTEGRATIONS_LINKS.map(link => (
                        <li key={link.href}>
                          <a
                            href={link.href}
                            className="block px-8 py-3 text-sm transition-colors text-neutral-600 hover:bg-neutral-100"
                          >
                            {link.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </li>
                </ul>

                {/* Divider */}
                <div className="h-px bg-neutral-200" />

                {/* Auth Section */}
                <ul className="flex flex-col w-full">
                  <li>
                    <a
                      href={`${STUDIO_URL}/login`}
                      className="block px-6 py-4 text-base font-medium transition-colors text-neutral-900 hover:bg-neutral-50"
                    >
                      Sign In
                    </a>
                  </li>
                  <li className="px-6 py-3">
                    <a
                      href={`${STUDIO_URL}/register`}
                      className="block w-full px-4 py-3 text-base font-medium text-center rounded-lg btn-primary"
                    >
                      Get it free
                    </a>
                  </li>
                </ul>
              </nav>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
